import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  Router,
  UrlTree,
} from '@angular/router';
import { COURTS_IDS } from '@app/core/constants/courts';
import { CourtService } from '@shared/services/court.service';

export const courtGuard: CanActivateChildFn = (
  route: ActivatedRouteSnapshot,
): boolean | UrlTree => {
  const courtService = inject(CourtService);
  const router = inject(Router);

  // Allow deep links that only need to open the login modal (e.g. after password reset).
  if (route.queryParams['open_login'] === '1') {
    return true;
  }

  // Allow password recovery deep links (token validation will handle errors).
  if (route.firstChild?.routeConfig?.path === 'password-recovery') {
    return true;
  }

  // Extract court from URL path parameters
  const court = route.pathFromRoot
    .flatMap((r) => Object.entries(r.params))
    .find(([key]) => key === 'court')?.[1];

  // Validate court exists and is a valid court key
  if (!court || !isValidCourtKey(court)) {
    return navigateToNotFound(router);
  }

  // Set court ID in service
  const courtId = COURTS_IDS[court];
  courtService.setCourtId(courtId);

  return true;
};

const navigateToNotFound = (router: Router): UrlTree => {
  return router.parseUrl('**');
};

const isValidCourtKey = (
  court: string | null | undefined,
): court is keyof typeof COURTS_IDS => {
  if (!court) return false;
  const courtUpperCase = court.toUpperCase();
  return Object.hasOwn(COURTS_IDS, courtUpperCase);
};
